import { Center, Flex, FormControl, Spinner, Text, Square, Box, Avatar } from '@chakra-ui/react';
import { AsyncSelect, chakraComponents, OptionProps } from 'chakra-react-select';
import { useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { t } from '@lingui/core/macro';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';

type StudentType = RouterOutputs['users']['studentById'];

interface StudentOption extends StudentType {
  label: string;
}

function Option(props: OptionProps<StudentOption>) {
  return (
    <chakraComponents.Option {...props}>
      <Flex justify="space-between">
        <Flex align="center" justify="center">
          <Box>
            <Square size="32px">
              <Avatar size="sm" name={props.data.label} src={props.data.picture_url ?? undefined} />
            </Square>
          </Box>
        </Flex>
        <Flex direction="column" ml={4}>
          <Box>
            <Text>{`${props.data.label}`}</Text>
            <Text fontSize="sm" color={'fg.muted'}>{`${props.data.email}`}</Text>
          </Box>
        </Flex>
      </Flex>
    </chakraComponents.Option>
  );
}

export function StudentSelection({
  student,
  onStudentSelected,
  disabledSelection,
  size = 'lg',
}: {
  student: StudentType | null | undefined;
  onStudentSelected: (student: StudentOption) => void;
  disabledSelection?: boolean;
  size?: 'sm' | 'md' | 'lg';
}) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const defaultState = student as StudentOption;
  const [formState, setFormState] = useState<StudentOption>(defaultState ?? null);
  const { data, isLoading } = trpcTenant.users.listStudentsWithPagination.useQuery({
    excludeInternalEnrolments: true,
  });

  const handleChange = (student: StudentOption) => {
    setFormState(student);
    onStudentSelected(student);
  };

  const studentOptions =
    data?.items.map((option) => ({
      ...option,
      value: option.id,
      label: `${option.first_name} ${option.last_name}`,
    })) || [];

  const loadStudentOptions = useDebounceCallback((inputValue: string, callback: any) => {
    if (studentOptions) {
      callback(
        studentOptions.filter(
          (option) =>
            option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
            option.email?.toLowerCase().includes(inputValue.toLowerCase()),
        ),
      );
    } else {
      callback([]);
    }
  }, 500);

  if (isLoading || !data) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  return (
    <Flex justify="center" align="center" w="full">
      <FormControl id="select_student">
        <AsyncSelect
          name="select_student"
          placeholder={t`Select or search a participant`}
          defaultOptions
          loadOptions={loadStudentOptions}
          onChange={(student) => {
            handleChange(student as StudentOption);
          }}
          value={formState}
          defaultValue={formState}
          closeMenuOnSelect={true}
          size={size}
          selectedOptionColorScheme={'brand'}
          isClearable={true}
          components={{ Option }}
          isDisabled={disabledSelection}
        />
      </FormControl>
    </Flex>
  );
}
